import { useHistory, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getOnboardingRoute as getSharedOnboardingRoute } from './onboardingUtils';

// Simple authentication check
export const isAuthenticated = (auth) => {
    return !!(auth && auth.user && auth.user.id);
};

// Paid subscriber check
export const isPaidSubscriber = (auth) => {
    if (!auth?.user?.permission?.subscription_current_period_end) return false;
    const subscriptionEnd = new Date(auth.user.permission.subscription_current_period_end);
    const now = new Date();
    return subscriptionEnd > now;
};

export const AuthorizationCheck = (auth, access='Private Beta', redirect=true) => {
    
    const [renderPage, setRenderPage] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [inviteCodeValue, setInviteCodeValue] = useState(false);

    const history = useHistory();
    const location = useLocation();

    const checkInviteCode = async (user_id) => {        
        
        const res = await axios.post('/api/post/get-invite-code', { user_id });
        //console.log(res.data.invite_code);
        if ( res.status === 200 ) {
            setInviteCode( res.data.invite_code );
        }

        if ( res.status >= 200 ) {
            setInviteCodeValue(true);
        }
    }

    useEffect(() => {
        
        if ( auth?.user?.id !== undefined ) {
            
                window.dataLayer = window.dataLayer || [];
            
                // Push userId to the dataLayer
                window.dataLayer.push({
                    userId: auth?.user?.id,
                });
        
            
        }
    }, [auth?.user?.id]); // Runs when the `userId` changes or on first render

    useEffect(() => {
        //console.log(auth?.user?.permission);
        switch( true ){
            case auth === null:
                // User is not authenticated, redirect to login for protected pages
                if (redirect && !location.pathname.includes('login') &&  !location.pathname.includes('onboarding')) {
                    history.push("/login");
                } else {
                    setRenderPage(false);
                }
                break;
            case Object.keys(auth).length === 0:
                // Auth object exists but is empty (no user data), redirect to login for protected pages
                if (redirect && !location.pathname.includes('login') &&  !location.pathname.includes('onboarding')) {
                    history.push("/login");
                } else {
                    setRenderPage(false);
                }
                break;
            case auth && auth?.user?.permission === undefined:
                //console.log(auth.user?.id);

                if ( auth.user?.id ) {
                    checkInviteCode( auth.user.id );
                }                

                if ( inviteCode !== '' && !location.pathname.includes('invite-signup') ) {
                    //console.log('redirect');
                    //console.log(location.pathname);
                    window.location.href = '/invite-signup/' + inviteCode;
                }
                else if ( redirect && inviteCodeValue ) {
                    window.location.href = '/api/logout-waitlist';
                }
                else {
                    setRenderPage( false );
                }
                break;
            case auth?.user?.permission !== undefined && auth?.user?.permission.show_onboarding !== false:

                if ( location.pathname.includes('onboarding') ) {
                    setRenderPage( true );
                    break;
                }
                else {
                    // For users in onboarding, don't force redirects based on database step
                    // Let the individual components handle their own validation
                    if (auth?.user?.permission.onboarding_step && auth?.user?.permission.onboarding_step < 8) {
                        setRenderPage(true);
                    } else if (auth?.user?.permission.onboarding_step === 8) {
                        // User reached plan selection, check if completed
                        if (auth?.user?.permission.show_onboarding === false) {
                            // Onboarding actually completed (plan selected)
                            setRenderPage(true);
                        } else if (location.pathname === '/dashboard' || location.pathname === '/settings' || location.pathname === '/contact') {
                            // Allow access to dashboard, settings, or contact if user is trying to access them
                            setRenderPage(true);
                        } else if (location.pathname.includes('onboarding')) {
                            // User is on an onboarding page, allow access
                            setRenderPage(true);
                        } else {
                            // User is on step 8 but not on plan page, redirect to plan selection
                            history.push("/onboarding/plan");
                        }
                    } else {
                        // User completed onboarding, redirect to dashboard
                        history.push("/dashboard");
                    }   
                }
                break;
            case auth?.user?.permission.type === 'private_beta':
                
                if (access === 'Private Beta' || access === 'user') {
                    setRenderPage( true );
                }
                else {
                    history.push("/dashboard");
                }
                break;
            case auth?.user?.permission.type === 'invite_beta':
                
                if (access === 'Private Beta' || access === 'user') {
                    setRenderPage( true );
                }
                else {
                    history.push("/dashboard");
                }
                break;
            case auth?.user?.permission.type === 'admin':
                    
                setRenderPage( true );
                break;
            default:
                setRenderPage( true );
                break;
        }
    }, [auth, history, access, location, redirect, inviteCode, inviteCodeValue]);
    
    return renderPage;
};

/**
 * Check if user has an active subscription
 * @param {Object} auth - The auth object from Redux state
 * @returns {boolean} - True if user has active subscription, false otherwise
 */
export const hasActiveSubscription = (auth) => {
    if (!auth?.user?.permission) {
        return false;
    }
    
    const subscriptionEnd = auth.user.permission.subscription_current_period_end;
    
    // Check if subscription is missing
    if (!subscriptionEnd) {
        return false;
    }
    
    // Check if subscription has expired (more than 1 day past due)
    const subscriptionEndDate = new Date(subscriptionEnd);
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    return subscriptionEndDate >= oneDayAgo;
};

// Onboarding completion check  
export const hasCompletedOnboarding = (auth) => {
    // Check if show_onboarding is explicitly false
    if (auth?.user?.permission?.show_onboarding === false) {
        return true;
    }
    
    // Also consider step 8 users with active subscriptions as completed
    if (auth?.user?.permission?.onboarding_step === 8 && 
        auth?.user?.permission?.subscription_current_period_end) {
        const subscriptionEnd = new Date(auth.user.permission.subscription_current_period_end);
        const now = new Date();
        // Check if subscription is still active (with 24 hour buffer)
        if (subscriptionEnd > new Date(now.getTime() - 24 * 60 * 60 * 1000)) {
            return true;
        }
    }
    
    return false;
};

// Get the correct onboarding route based on user's current step
export const getOnboardingRoute = (auth) => {
    const step = auth?.user?.permission?.onboarding_step || 1;
    return getSharedOnboardingRoute(step);
};